/* Avslutning av en bankavstemming — én sekvens, brukt av begge modusene.
 *
 * Banklinjer bokføres nå når hver linje godkjennes. Avslutningen er derfor bare
 * kontroll og arkivering av utskriften.
 */
(function registerBankReconciliationFinish(scope) {
  const tr = (key) => (typeof scope.t === 'function' ? scope.t(key) : key);
  // Med norsk reservetekst og variabler: kjøres bare ved bruk (aldri ved lasting),
  // og faller tilbake til norsk hvis katalogen ikke er lastet.
  const trf = (key, fallback, variables) => (typeof scope.t === 'function'
    ? scope.t(key, fallback, variables)
    : Object.entries(variables || {}).reduce(
      (message, [name, value]) => message.replaceAll(`{${name}}`, String(value)),
      fallback,
    ));
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const text = (value) => (typeof value === 'string' ? value : '');

  // Samme kriterium som `bankStatementSettled` i
  // functions/bank/bankReconciliationMutationHttp.ts og `apReconciledMonths` i
  // src/pages/autopilot-model.jsx. En utskrift er ferdig naar backend sier det —
  // ikke naar alle linjene tilfeldigvis er behandlet.
  const SETTLED_STATUSES = ['archived', 'reconciled', 'booked'];

  const bankStatementSettled = (statement) => {
    const raw = statement?._backend || statement || {};
    if (raw.bookedAt || raw.reconciledAt) return true;
    return SETTLED_STATUSES.includes(text(statement?.status) || text(raw.status));
  };

  const bankStatementSettledAt = (statement) => {
    const raw = statement?._backend || statement || {};
    return text(raw.reconciledAt) || text(raw.bookedAt) || '';
  };

  // Legacy-lesere beholdes for eldre tester/verktøy, men arkivering bokfører ikke
  // lenger pending linjer. Nye godkjenninger bokføres per linje.
  const pendingBookingLines = (lines) => safeArray(lines).filter((line) => {
    const raw = line?._backend || {};
    return raw.reconciliationStatus === 'approved' && raw.classification === 'business' && !raw.matchedEntryId && !raw.bankMatchGroupId;
  });

  const pendingMatchGroupIds = (lines) => [...new Set(safeArray(lines)
    .map((line) => line?._backend || line || {})
    .filter((raw) => raw.reconciliationStatus === 'approved' && raw.bankMatchGroupId && !raw.matchedEntryId)
    .map((raw) => text(raw.bankMatchGroupId)))];

  const pendingAutopilotTaskIds = (lines) => safeArray(lines)
    .filter((line) => {
      const raw = line?._backend || line || {};
      return line?.approved && line?.approvalTaskId && !raw.matchedEntryId && !raw.bookedEntryId;
    })
    .map((line) => line.approvalTaskId);

  const phaseError = (phase, error, fallback) => {
    const message = error?.message || fallback;
    const next = new Error(message);
    next.phase = phase;
    next.cause = error;
    next.details = error?.details || null;
    next.status = error?.status;
    next.code = error?.code || '';
    return next;
  };

  /**
   * Arkiverer en ferdig kontrollert bankutskrift.
   *
   * Returnerer `{ status }`: `'already-settled'` naar utskriften alt er avstemt,
   * ellers `'settled'`. Kaster med `error.phase` satt til `'assert'` eller
   * `'archive'`, slik at kalleren kan si hva som faktisk feilet.
   */
  const finishBankStatementReconciliation = async ({ backend, statement, lines = [], onProgress } = {}) => {
    const api = backend || scope.PlummeBackend;
    if (!api?.archiveBankStatement) throw new Error(tr('r1e.reconFinish.unavailable'));
    const statementId = text(statement?.id) || text(statement);
    if (!statementId) throw new Error(tr('r1e.reconFinish.statementMissing'));

    // En ferdig utskrift skal ikke kjore sekvensen om igjen. Uten denne sperren
    // ble allerede utforte autopilot-oppgaver forsokt bokfort paa nytt, og
    // brukeren fikk «Autopilot task must be approved before bookkeeping».
    if (statement && typeof statement === 'object' && bankStatementSettled(statement)) {
      return { status: 'already-settled', bookedLineCount: 0 };
    }

    try {
      await api.assertCanBookBankStatement?.(statementId);
    } catch (error) {
      throw phaseError('assert', error, trf('rcb.finish.assertFailed', 'Bankutskriften kan ikke avstemmes ennå.'));
    }

    onProgress?.('archive');
    try {
      await api.archiveBankStatement?.(statementId);
    } catch (error) {
      throw phaseError('archive', error, trf('rcb.finish.archiveRejected', 'Arkiveringen ble avvist.'));
    }

    await api.logBankSuggestionOutcome?.({ statementId, lines, outcome: 'booked' }).catch((error) => {
      console.warn('Kunne ikke logge bankfasit for AI-fasit.', error);
    });

    return { status: 'settled', bookedLineCount: 0 };
  };

  const finishErrorMessage = (error) => {
    const details = error?.details || error?.cause?.details || null;
    if (details?.reason === 'bank-ledger-mismatch') {
      const booked = Number(details.booked);
      const bank = Number(details.bank);
      const difference = Number.isFinite(Number(details.difference))
        ? Number(details.difference)
        : booked - bank;
      if (Number.isFinite(booked) && Number.isFinite(bank) && Number.isFinite(difference)) {
        const number = (value) => scope.PlummeI18n?.number
          ? scope.PlummeI18n.number(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : value.toFixed(2);
        const date = details.end && scope.PlummeI18n?.date
          ? scope.PlummeI18n.date(details.end)
          : text(details.end);
        const translate = typeof scope.t === 'function'
          ? scope.t
          : (_key, fallback, variables) => Object.entries(variables || {}).reduce(
            (message, [key, value]) => message.replaceAll(`{{${key}}}`, String(value)),
            fallback,
          );
        return translate(
          'bank.archiveLedgerMismatch',
          'Kan ikke arkivere: bokført saldo per {{date}} er {{booked}} kr, mens bankens saldo er {{bank}} kr. Avvik {{difference}} kr.',
          { date, booked: number(booked), bank: number(bank), difference: number(difference) },
        );
      }
    }
    if (details?.code === 'bank.archiveBlocked') {
      if (Number(details.unhandledLineCount || 0) > 0) {
        const lines = safeArray(details.unhandledLines);
        const first = lines[0];
        const label = first?.id ? ` (${first.id})` : '';
        const status = first?.status ? `: ${first.status}` : '';
        return trf('rcb.finish.unhandledLines', 'Kan ikke arkivere ennå: {count} banklinje(r) er ikke ferdig håndtert{label}{status}.', { count: details.unhandledLineCount, label, status });
      }
      const deviation = Number(details.balanceDeviation || 0);
      if (Number.isFinite(deviation) && Math.abs(deviation) >= 0.005) {
        return trf('rcb.finish.balanceDeviation', 'Kan ikke arkivere ennå: saldoavviket er {deviation} kr.', { deviation: deviation.toFixed(2) });
      }
    }
    const message = error?.message || trf('rcb.finish.failed', 'Kunne ikke avstemme bankutskriften.');
    return message;
  };

  scope.PlummeBankReconciliationFinish = {
    bankStatementSettled,
    bankStatementSettledAt,
    pendingBookingLines,
    pendingMatchGroupIds,
    pendingAutopilotTaskIds,
    finishBankStatementReconciliation,
    finishErrorMessage,
  };
})(typeof window !== 'undefined' ? window : globalThis);
